import React, { useState } from 'react';

const FONT_MAP = {
  'Arial': 'Arial, sans-serif',
  'Helvetica': 'Helvetica, Arial, sans-serif',
  'Georgia': 'Georgia, serif',
  'Courier': '"Courier New", Courier, monospace',
  'Verdana': 'Verdana, Geneva, sans-serif',
  'Impact': 'Impact, sans-serif',
};

const SIZE_MAP = { small: 0.12, medium: 0.18, large: 0.26 };

const Box3D = ({ 
  length = 300, width = 200, height = 150, 
  artworkUrl = null, rotation = { x: -25, y: -35 },
  coveragePercent = 100, printSide = 'front',
  isWhiteMaterial = false, textOverlay = null, textOverlays = null,
}) => {
  const safeLength = Math.max(parseFloat(length) || 100, 50);
  const safeWidth = Math.max(parseFloat(width) || 100, 50);
  const safeHeight = Math.max(parseFloat(height) || 100, 50);
  
  const maxDim = Math.max(safeLength, safeWidth, safeHeight);
  const scale = 120 / maxDim;
  const w = safeLength * scale;
  const h = safeHeight * scale;
  const d = safeWidth * scale;

  const flapRatio = (safeWidth / 2) / safeHeight;
  const flapPx = Math.min(h * flapRatio, h * 0.45);

  const colors = isWhiteMaterial ? {
    front: 'linear-gradient(145deg, #F5F5F0 0%, #EAEAE5 100%)',
    back: 'linear-gradient(145deg, #EAEAE5 0%, #E0E0DB 100%)',
    left: 'linear-gradient(145deg, #F0F0EB 0%, #E5E5E0 100%)',
    right: 'linear-gradient(145deg, #EAEAE5 0%, #E0E0DB 100%)',
    top: 'linear-gradient(145deg, #FAFAF5 0%, #F0F0EB 100%)',
    bottom: 'linear-gradient(145deg, #E0E0DB 0%, #D5D5D0 100%)',
    border: 'rgba(180, 180, 175, 0.25)',
    flapLine: 'rgba(160, 160, 155, 0.5)',
  } : {
    front: 'linear-gradient(145deg, #D6C0B0 0%, #C9B3A3 100%)',
    back: 'linear-gradient(145deg, #C9B3A3 0%, #BCA799 100%)',
    left: 'linear-gradient(145deg, #CDBAA8 0%, #C0A898 100%)',
    right: 'linear-gradient(145deg, #C9B3A3 0%, #BCA799 100%)',
    top: 'linear-gradient(145deg, #E0D0C0 0%, #D6C0B0 100%)',
    bottom: 'linear-gradient(145deg, #BCA799 0%, #A99789 100%)',
    border: 'rgba(45, 42, 38, 0.15)',
    flapLine: 'rgba(80, 60, 45, 0.35)',
  };

  const coverageScale = Math.sqrt(coveragePercent / 100);
  
  const getFaceStyle = (fw, fh, bg) => ({
    position: 'absolute', width: `${fw}px`, height: `${fh}px`,
    background: bg, border: `1px solid ${colors.border}`,
    boxShadow: 'inset 0 0 20px rgba(0,0,0,0.03)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  });

  const ArtworkOverlay = ({ faceWidth, faceHeight }) => {
    if (!artworkUrl) return null;
    return (
      <div style={{
        width: `${faceWidth * coverageScale}px`, height: `${faceHeight * coverageScale}px`,
        backgroundImage: `url(${artworkUrl})`, backgroundSize: 'cover', backgroundPosition: 'center',
        borderRadius: '2px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }} />
    );
  };

  const TextOverlayEl = ({ faceWidth, faceHeight, textOverlay: to }) => {
    const overlay = to || textOverlay;
    if (!overlay?.text_content) return null;
    const { text_content, text_color, font_family, font_size, text_alignment } = overlay;
    const sizeRatio = SIZE_MAP[font_size] || SIZE_MAP.medium;
    const maxFontPx = Math.min(faceHeight * sizeRatio, faceWidth * 0.35);
    const fontSize = Math.max(6, maxFontPx);
    const padding = Math.max(2, faceWidth * 0.06);

    const alignMap = { left: 'flex-start', right: 'flex-end', center: 'center' };

    return (
      <div style={{
        position: 'absolute', inset: `${padding}px`,
        display: 'flex', alignItems: 'center',
        justifyContent: alignMap[text_alignment] || 'center',
        textAlign: text_alignment || 'center',
        overflow: 'hidden', pointerEvents: 'none', zIndex: 3,
      }}>
        <span style={{
          color: text_color || '#222222',
          fontFamily: FONT_MAP[font_family] || font_family || 'Arial, sans-serif',
          fontSize: `${fontSize}px`, fontWeight: 700,
          lineHeight: 1.15, wordBreak: 'break-word',
          maxWidth: '100%',
          textShadow: isWhiteMaterial ? 'none' : '0 0.5px 1px rgba(0,0,0,0.15)',
        }}>
          {text_content}
        </span>
      </div>
    );
  };

  const TopFlapSlit = () => (
    <div style={{
      position: 'absolute', top: '50%', left: '6%', right: '6%',
      height: 0, borderTop: `1px dashed ${colors.flapLine}`,
      pointerEvents: 'none', zIndex: 2, opacity: 0.7,
    }} />
  );

  const shouldShowContent = (face) => {
    if (printSide === 'all') return true;
    return printSide === face;
  };

  const hasArtwork = !!artworkUrl;
  const hasText = !!textOverlay?.text_content;

  const FaceContent = ({ face, fw, fh }) => {
    if (!shouldShowContent(face) && !textOverlays) return null;
    if (hasArtwork && shouldShowContent(face)) return <ArtworkOverlay faceWidth={fw} faceHeight={fh} />;
    // Multi-face text: check per-face overlay first
    const perFace = textOverlays?.[face];
    if (perFace?.text_content) return <TextOverlayEl faceWidth={fw} faceHeight={fh} textOverlay={perFace} />;
    // Fallback: single text overlay on selected printSide
    if (hasText && shouldShowContent(face)) return <TextOverlayEl faceWidth={fw} faceHeight={fh} textOverlay={textOverlay} />;
    return null;
  };

  return (
    <div className="w-full flex items-center justify-center" style={{ minHeight: '260px', perspective: '600px' }}>
      <div style={{
        width: `${w}px`, height: `${h}px`, position: 'relative',
        transformStyle: 'preserve-3d',
        transform: `rotateX(${rotation.x}deg) rotateY(${rotation.y}deg)`,
        transition: 'transform 0.2s ease-out',
      }}>
        <div style={{ ...getFaceStyle(w, h, colors.front), transform: `translateZ(${d / 2}px)`, left: 0, top: 0 }}>
          <FaceContent face="front" fw={w} fh={h} />
        </div>
        <div style={{ ...getFaceStyle(w, h, colors.back), transform: `translateZ(${-d / 2}px) rotateY(180deg)`, left: 0, top: 0 }}>
          <FaceContent face="back" fw={w} fh={h} />
        </div>
        <div style={{ ...getFaceStyle(d, h, colors.left), transform: `translateX(${-d / 2}px) rotateY(-90deg)`, left: 0, top: 0 }}>
          <FaceContent face="left" fw={d} fh={h} />
        </div>
        <div style={{ ...getFaceStyle(d, h, colors.right), transform: `translateX(${w - d / 2}px) rotateY(90deg)`, left: 0, top: 0 }}>
          <FaceContent face="right" fw={d} fh={h} />
        </div>
        <div style={{ ...getFaceStyle(w, d, colors.top), transform: `translateY(${-d / 2}px) rotateX(90deg)`, left: 0, top: 0 }}>
          <TopFlapSlit />
          <FaceContent face="top" fw={w} fh={d} />
        </div>
        <div style={{ ...getFaceStyle(w, d, colors.bottom), transform: `translateY(${h - d / 2}px) rotateX(-90deg)`, left: 0, top: 0 }}>
          <TopFlapSlit />
        </div>
      </div>
    </div>
  );
};

export const Box3DInteractive = ({
  length, width, height, artworkUrl, coveragePercent = 100,
  printSide = 'front', isWhiteMaterial = false, textOverlay = null, textOverlays = null,
}) => {
  const [rotation, setRotation] = useState({ x: -20, y: -30 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => { setIsDragging(true); setLastPos({ x: e.clientX, y: e.clientY }); };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setRotation(prev => ({
      x: Math.max(-60, Math.min(60, prev.x - (e.clientY - lastPos.y) * 0.5)),
      y: prev.y + (e.clientX - lastPos.x) * 0.5
    }));
    setLastPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseUp = () => setIsDragging(false);
  const handleTouchStart = (e) => { const t = e.touches[0]; setIsDragging(true); setLastPos({ x: t.clientX, y: t.clientY }); };
  const handleTouchMove = (e) => {
    if (!isDragging) return;
    const t = e.touches[0];
    setRotation(prev => ({
      x: Math.max(-60, Math.min(60, prev.x - (t.clientY - lastPos.y) * 0.5)),
      y: prev.y + (t.clientX - lastPos.x) * 0.5
    }));
    setLastPos({ x: t.clientX, y: t.clientY });
  };

  return (
    <div className="cursor-grab active:cursor-grabbing select-none"
      onMouseDown={handleMouseDown} onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleMouseUp}
    >
      <Box3D length={length} width={width} height={height}
        artworkUrl={artworkUrl} rotation={rotation}
        coveragePercent={coveragePercent} printSide={printSide}
        isWhiteMaterial={isWhiteMaterial} textOverlay={textOverlay}
        textOverlays={textOverlays}
      />
      <p className="text-center text-xs text-muted-foreground mt-2">Arrastra para rotar la caja</p>
    </div>
  );
};

export default Box3D;
